import React from 'react';

interface YearSelectorBarProps {
  year: number;
  setYear: (year: number | ((prev: number) => number)) => void;
  earliestYear?: number;
  currentYear?: number;
}

const YearSelectorBar: React.FC<YearSelectorBarProps> = ({
  year,
  setYear,
  earliestYear = 2025,
  currentYear = new Date().getFullYear(),
}) => {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3 bg-gradient-to-r from-gray-900 to-gray-700 rounded-xl mb-6 shadow-md">
      <div className="flex items-center gap-2 text-white">
        <svg
          className="h-4 w-4 opacity-70"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="text-xs font-baijamjuree uppercase tracking-widest opacity-70">
          Award Year
        </span>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setYear((y: number) => Math.max(y - 1, earliestYear))}
          disabled={year <= earliestYear}
          aria-label="Previous year"
          className="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <div className="flex gap-1">
          {Array.from(
            { length: currentYear - earliestYear + 1 },
            (_, i) => earliestYear + i
          ).map(y => (
            <button
              key={y}
              onClick={() => setYear(y)}
              className={`px-3 py-1 rounded-full text-sm font-baijamjuree font-bold transition-all duration-200 ${
                y === year
                  ? 'bg-white text-gray-900 shadow-md scale-105'
                  : 'text-white/70 hover:text-white hover:bg-white/15'
              }`}
            >
              {y}
            </button>
          ))}
        </div>

        <button
          onClick={() => setYear((y: number) => Math.min(y + 1, currentYear))}
          disabled={year >= currentYear}
          aria-label="Next year"
          className="p-1 rounded-full text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-25 disabled:cursor-not-allowed transition-all"
        >
          <svg
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      <div className="flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        <span className="text-xs font-baijamjuree text-white font-semibold">
          Showing {year}
        </span>
      </div>
    </div>
  );
};

export default YearSelectorBar;
